import {
  IonSpinner,
  IonText,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import type { RefresherCustomEvent } from '@ionic/react';
import type { PostCardFieldsFragment } from '../../../../types/graphql-generated';
import { PostCard } from './PostCard';

interface PostFeedProps {
  posts: PostCardFieldsFragment[];
  loading: boolean;
  error: unknown;
  hasMore: boolean;
  emptyMessage: string;
  onLoadMore: (ev: CustomEvent<void>) => void | Promise<void>;
  onRefresh: (ev: RefresherCustomEvent) => void | Promise<void>;
}

// Shared presentational feed for channel posts: pull-to-refresh, the
// loading/error/empty states, the post list and infinite-scroll pagination.
// Used by both the followed-channels timeline and a single channel's posts;
// data fetching lives in the usePaginatedPosts hook.
export const PostFeed = ({
  posts,
  loading,
  error,
  hasMore,
  emptyMessage,
  onLoadMore,
  onRefresh,
}: PostFeedProps) => {
  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      {loading && posts.length === 0 && (
        <div className="flex justify-center items-center min-h-[200px]">
          <IonSpinner name="crescent" />
        </div>
      )}

      {/* Only surface the error when there is nothing to show. With
          cache-and-network a transient background-refetch failure must not
          replace an already-populated (cached) list with an error banner. */}
      {error && posts.length === 0 && (
        <IonText color="danger">
          <p>Error al cargar las publicaciones</p>
        </IonText>
      )}

      {!loading && !error && posts.length === 0 && (
        <div className="flex justify-center items-center min-h-[200px]">
          <IonText color="medium">
            <p>{emptyMessage}</p>
          </IonText>
        </div>
      )}

      {posts.length > 0 && (
        <>
          <div>
            {posts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>

          <IonInfiniteScroll onIonInfinite={onLoadMore} threshold="100px" disabled={!hasMore}>
            <IonInfiniteScrollContent loadingSpinner="bubbles" loadingText="Cargando más..." />
          </IonInfiniteScroll>
        </>
      )}
    </>
  );
};
